import React, { useEffect, useMemo, useState } from "react";
import { FiSearch, FiSliders } from "react-icons/fi";
import { HiChevronDown, HiDotsVertical } from "react-icons/hi";
import { GoDotFill } from "react-icons/go";
import { LuWrench } from "react-icons/lu";
import { useDispatch, useSelector } from "react-redux";

import CorrectiveActionWorkflow from "./CorrectiveActionWorkflow";
import { getIncidents } from "../../../features/incidents/incidentActions.js";

// ============================================================
// INCIDENT MAPPER
// Converts API incident data into table-friendly data
// ============================================================

const mapIncidentToTable = (incident) => {
  const categoryMap = {
    CUSTOMER_INJURY: "Customer Injury",
    PROPERTY_DAMAGE: "Property Damage",
    STAFF_INJURY: "Staff Injury",
    SECURITY_INCIDENT: "Security Incident",
    FOOD_SPILL: "Food Spill",
    ALLERGEN_DIETARY: "Allergen / Dietary",
    EQUIPMENT_FAILURE: "Equipment Failure",
    CUSTOMER_COMPLAINT: "Customer Complaint",
    FIRE_SAFETY: "Fire Safety",
    OTHER: "Other",
  };

  const statusMap = {
    OPEN: "Open",
    IN_PROGRESS: "In Progress",
    COMPLETED: "Completed",
  };

  const statusBackgrounds = {
    OPEN: "bg-orange-50 text-orange-700",
    IN_PROGRESS: "bg-blue-50 text-blue-700",
    COMPLETED: "bg-emerald-50 text-emerald-700",
  };

  const categoryBackgrounds = {
    CUSTOMER_INJURY: "bg-purple-50 text-purple-700",
    STAFF_INJURY: "bg-purple-50 text-purple-700",
    PROPERTY_DAMAGE: "bg-amber-50 text-amber-700",
    SECURITY_INCIDENT: "bg-red-50 text-red-700",
    FOOD_SPILL: "bg-rose-50 text-rose-600",
    ALLERGEN_DIETARY: "bg-orange-50 text-orange-700",
    EQUIPMENT_FAILURE: "bg-blue-50 text-blue-700",
    CUSTOMER_COMPLAINT: "bg-cyan-50 text-cyan-700",
    FIRE_SAFETY: "bg-red-50 text-red-700",
    OTHER: "bg-gray-50 text-gray-700",
  };

  return {
    // Keep original API object available.
    // CorrectiveActionWorkflow may need fields from the API.
    ...incident,

    // ============================================================
    // BASIC
    // ============================================================

    id: incident.id,

    title: incident.description || "Untitled incident",

    category:
      categoryMap[incident.type] || incident.type || "Unknown",

    categoryBg:
      categoryBackgrounds[incident.type] ||
      "bg-gray-50 text-gray-700",

    location: incident.location || "-",

    // ============================================================
    // STATUS
    // ============================================================

    status:
      statusMap[incident.status] ||
      incident.status ||
      "Unknown",

    statusBg:
      statusBackgrounds[incident.status] ||
      "bg-gray-50 text-gray-700",

    // ============================================================
    // REPORTED DATE
    // ============================================================

    reportedDate: incident.incidentDateTime
      ? new Date(incident.incidentDateTime).toLocaleString(
          "en-GB",
          {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          }
        )
      : "-",

    // ============================================================
    // REPORTED BY
    // ============================================================

    reportedBy:
      incident.createdBy?.fullName || "Unknown",

  };
};

// ============================================================
// COMPONENT
// ============================================================

export default function IncidentTableComponent() {
  const dispatch = useDispatch();

  // ============================================================
  // REDUX
  // ============================================================

  const {
    incidents,
    listLoading,
    listError,
  } = useSelector((state) => state.incidents);

  // ============================================================
  // LOCAL UI STATE
  // ============================================================

  const [selectedIncident, setSelectedIncident] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");

  const [selectedLocation, setSelectedLocation] =
    useState("All Locations");

  // ============================================================
  // FETCH INCIDENTS
  // ============================================================

  useEffect(() => {
    dispatch(getIncidents());
  }, [dispatch]);

  // ============================================================
  // MAP API DATA
  // ============================================================

  const incidentData = useMemo(() => {
    return incidents.map(mapIncidentToTable);
  }, [incidents]);

  // ============================================================
  // LOCATIONS
  // ============================================================

  const locations = useMemo(() => {
    const uniqueLocations = [
      ...new Set(
        incidentData
          .map((incident) => incident.location)
          .filter(Boolean)
      ),
    ];

    return ["All Locations", ...uniqueLocations];
  }, [incidentData]);

  // ============================================================
  // FILTER INCIDENTS
  // ============================================================

  const filteredIncidents = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();

    return incidentData.filter((incident) => {
      // Search
      const matchesSearch =
        !search ||
        incident.id?.toString().toLowerCase().includes(search) ||
        incident.title?.toLowerCase().includes(search) ||
        incident.category?.toLowerCase().includes(search) ||
        incident.location?.toLowerCase().includes(search) ||
        incident.reportedBy?.toLowerCase().includes(search);

      // Location
      const matchesLocation =
        selectedLocation === "All Locations" ||
        incident.location === selectedLocation;

      return matchesSearch && matchesLocation;
    });
  }, [
    incidentData,
    searchTerm,
    selectedLocation,
  ]);

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div className="w-full p-6 font-sans bg-white border border-gray-100/80 rounded-3xl shadow-sm">

      {/* ======================================================
          ERROR
      ====================================================== */}

      {listError && (
        <div className="mb-4 px-4 py-3 rounded-xl bg-red-50 border border-red-100 text-sm text-red-600">
          {listError}
        </div>
      )}

      {/* ======================================================
          FILTERS AND SEARCH CONTROL BAR
      ====================================================== */}

      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">

        {/* LEFT SIDE */}
        <div className="flex flex-wrap items-center gap-2 flex-1 min-w-[300px]">

          {/* SEARCH */}
          <div className="relative w-full max-w-[200px]">
            <input
              type="text"
              placeholder="Search incidents..."
              value={searchTerm}
              onChange={(e) =>
                setSearchTerm(e.target.value)
              }
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-xl text-xs text-gray-700 placeholder-gray-400 focus:outline-none focus:border-blue-500 bg-white"
            />

            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          </div>

          {/* LOCATION FILTER */}
          <div className="relative">
            <select
              value={selectedLocation}
              onChange={(e) =>
                setSelectedLocation(e.target.value)
              }
              className="appearance-none pr-8 flex items-center gap-1.5 border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 px-3 py-2 rounded-xl text-xs font-semibold focus:outline-none"
            >
              {locations.map((location) => (
                <option
                  key={location}
                  value={location}
                >
                  {location}
                </option>
              ))}
            </select>

            <HiChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          </div>
        </div>

        {/* FILTER BUTTON */}
        <button
          type="button"
          className="flex items-center gap-1.5 border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-xl text-xs font-semibold shadow-sm"
        >
          <FiSliders className="w-3.5 h-3.5" />

          <span>Filters</span>
        </button>
      </div>

      {/* ======================================================
          LOADING
      ====================================================== */}

      {listLoading && (
        <div className="py-12 text-center text-sm text-gray-500">
          Loading incidents...
        </div>
      )}

      {/* ======================================================
          EMPTY
      ====================================================== */}

      {!listLoading &&
        !listError &&
        filteredIncidents.length === 0 && (
          <div className="py-12 text-center text-sm text-gray-500">
            {incidentData.length === 0
              ? "No incidents found."
              : "No incidents match your search or filters."}
          </div>
        )}

      {/* ======================================================
          TABLE
      ====================================================== */}

      {!listLoading &&
        !listError &&
        filteredIncidents.length > 0 && (
          <div className="w-full overflow-x-auto rounded-xl border border-gray-100">

            <table className="w-full text-left border-collapse min-w-[980px]">

              {/* ==================================================
                  TABLE HEADER
              ================================================== */}

              <thead>
                <tr className="bg-gray-50/50 border-b border-gray-100">

                  <th className="p-4 text-[11px] font-bold text-gray-400 uppercase tracking-wider w-[12%]">
                    ID
                  </th>

                  <th className="p-4 text-[11px] font-bold text-gray-400 uppercase tracking-wider w-[24%]">
                    Incident Title
                  </th>

                  <th className="p-4 text-[11px] font-bold text-gray-400 uppercase tracking-wider w-[14%]">
                    Category
                  </th>

                  <th className="p-4 text-[11px] font-bold text-gray-400 uppercase tracking-wider w-[12%]">
                    Location
                  </th>

                  <th className="p-4 text-[11px] font-bold text-gray-400 uppercase tracking-wider w-[10%]">
                    Status
                  </th>

                  <th className="p-4 text-[11px] font-bold text-gray-400 uppercase tracking-wider w-[16%]">
                    Reported
                  </th>

                  <th className="p-4 text-[11px] font-bold text-gray-400 uppercase tracking-wider w-[10%] text-center">
                    Corrective Action
                  </th>

                  <th className="p-4 text-[11px] font-bold text-gray-400 uppercase tracking-wider w-[5%] text-center">
                    Actions
                  </th>
                </tr>
              </thead>

              {/* ==================================================
                  TABLE BODY
              ================================================== */}

              <tbody className="divide-y divide-gray-100 text-xs">

                {filteredIncidents.map((incident) => (
                  <tr
                    key={incident.id}
                    className="hover:bg-gray-50/30 transition-colors"
                  >

                    {/* ID */}
                    <td className="p-4 font-semibold text-gray-600 tracking-tight">
                      {incident.id}
                    </td>

                    {/* TITLE */}
                    <td className="p-4 font-bold text-gray-900 tracking-tight">
                      {incident.title}
                    </td>

                    {/* CATEGORY */}
                    <td className="p-4">
                      <span
                        className={`inline-block px-2.5 py-1 text-[10px] font-bold rounded-lg ${incident.categoryBg}`}
                      >
                        {incident.category}
                      </span>
                    </td>

                    {/* LOCATION */}
                    <td className="p-4 font-medium text-gray-700">
                      {incident.location}
                    </td>

                    {/* STATUS */}
                    <td className="p-4">
                      <span
                        className={`inline-block px-2.5 py-1 text-[10px] font-bold rounded-lg ${incident.statusBg}`}
                      >
                        {incident.status}
                      </span>
                    </td>

                    {/* REPORTED */}
                    <td className="p-4 text-gray-500 font-medium">
                      {incident.reportedDate}
                    </td>

                    {/* CORRECTIVE ACTION */}
                    <td className="p-4 text-center">
                      <button
                        type="button"
                        onClick={() =>
                          setSelectedIncident(incident)
                        }
                        className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-600 border border-emerald-200 bg-emerald-50/50 hover:bg-emerald-50 px-2.5 py-1.5 rounded-xl shadow-xs transition-colors"
                      >
                        <LuWrench className="text-xs" />
                        <span>
                          Manage Action
                        </span>
                      </button>
                    </td>

                    {/* ACTIONS */}
                    <td className="p-4 text-center">
                      <button
                        type="button"
                        className="text-gray-400 hover:text-gray-600 p-1"
                      >
                        <HiDotsVertical className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}

              </tbody>
            </table>
          </div>
        )}

      {/* ======================================================
          CORRECTIVE ACTION WORKFLOW
      ====================================================== */}

      {selectedIncident && (
        <CorrectiveActionWorkflow
          incident={selectedIncident}
          onClose={() =>
            setSelectedIncident(null)
          }
        />
      )}

    </div>
  );
}